// src/components/Finanzas/hooks/useFinanzasData.js
// Data layer for the Finanzas module. Reads only the v_finanzas_* views.
//
// The views are security_invoker, so tenant RLS scopes every row — never filter
// by tenant here. Every figure the UI shows is a view column; no money math here.
//
// Every hook returns { loading, error, data, refetch, viewsMissing }. `error` is
// always a Spanish string for the UI; the raw error only goes to the console.
// Each hook guards against stale responses with a sequence counter, so a slow
// request for a previous period can never overwrite a fast one for the current.

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import { calendarMonthRange } from '../utils/timeBoundaries';

// ── Views availability probe ──────────────────────────────────────────────────
// Flipped to false on the first "relation does not exist" error and never
// retried for the rest of the page load. 42P01 is Postgres' undefined_table;
// PGRST205 is how current PostgREST reports a relation missing from its cache.
let finanzasViewsAvailable = true;

export const VIEWS_MISSING = Object.freeze({ viewsMissing: true });

class ViewsMissingError extends Error {}

const isMissingRelation = (error) => error?.code === '42P01' || error?.code === 'PGRST205';

// Await a PostgREST builder; return data or throw.
const run = async (query) => {
  const { data, error } = await query;
  if (error) {
    if (isMissingRelation(error)) {
      if (finanzasViewsAvailable) {
        finanzasViewsAvailable = false;
        console.error('[FINANZAS] vistas v_finanzas_* no disponibles — se omiten las consultas hasta recargar.', error);
      }
      throw new ViewsMissingError(error.message);
    }
    throw error;
  }
  return data;
};

// Short-circuit once the views are known to be missing; map the probe error to the sentinel.
const withViews = async (load) => {
  if (!finanzasViewsAvailable) return VIEWS_MISSING;
  try {
    return await load();
  } catch (err) {
    if (err instanceof ViewsMissingError) return VIEWS_MISSING;
    throw err;
  }
};

const ERROR_MSG = {
  manifiesto:  'No se pudieron cargar las finanzas del manifiesto. Intentá de nuevo.',
  manifiestos: 'No se pudo cargar la lista de manifiestos. Intentá de nuevo.',
  mes:         'No se pudieron cargar las finanzas del mes. Intentá de nuevo.',
  excepciones: 'No se pudieron cargar las excepciones. Intentá de nuevo.',
  operadores:  'No se pudo cargar el resumen por operador. Intentá de nuevo.',
  alerta:      'No se pudo verificar la clasificación de impuestos. Intentá de nuevo.',
  rango:       'No se pudieron cargar los totales del período. Intentá de nuevo.',
  facturas:    'No se pudieron cargar las facturas del período. Intentá de nuevo.',
  serie:       'No se pudo cargar la gráfica de ingreso. Intentá de nuevo.',
};

export const GRANOS = ['day', 'week', 'month'];

// Flag column → Spanish reason shown on an exception row.
export const MOTIVO_LABELS = {
  flag_sobrepago:        'Sobrepago',
  flag_anulada_con_pago: 'Anulada con pago',
  flag_descuadre:        'Descuadre',
  flag_drift:            'Total desfasado',
};

const IDLE = { loading: false, error: null, data: null, viewsMissing: false };
const LOADING = { loading: true, error: null, data: null, viewsMissing: false };

const settled = (result) =>
  result === VIEWS_MISSING
    ? { loading: false, error: null, data: null, viewsMissing: true }
    : { loading: false, error: null, data: result, viewsMissing: false };

// ── Fetch functions ───────────────────────────────────────────────────────────

/** One manifest's summary row plus its invoices. */
export function fetchManifiestoFinanzas(manifestId) {
  return withViews(async () => {
    const [manifiesto, facturas] = await Promise.all([
      run(supabase.from('v_finanzas_manifiesto').select('*').eq('manifest_id', manifestId).maybeSingle()),
      run(supabase.from('v_finanzas_factura').select('*').eq('manifest_id', manifestId).order('invoice_number')),
    ]);
    return { manifiesto, facturas: facturas || [] };
  });
}

/** Most recent manifests, newest opened first. */
export function fetchManifiestosFinanzas(limit = 24) {
  return withViews(async () => {
    const rows = await run(
      supabase.from('v_finanzas_manifiesto').select('*')
        .order('abierto', { ascending: false, nullsFirst: false })
        .limit(limit)
    );
    return rows || [];
  });
}

/**
 * A month's summary, its collections by payment method, and the invoices issued
 * in it. `periodo` may be any date inside the month ('2026-09-01', '2026-09-17').
 * mensual is anchored on issue date, cobranza on payment date — by design.
 */
export function fetchMesFinanzas(periodo) {
  return withViews(async () => {
    const { startStr, endStr, label } = calendarMonthRange(periodo);
    const [mensual, cobranza, facturas] = await Promise.all([
      run(supabase.from('v_finanzas_mensual').select('*').eq('periodo', startStr).maybeSingle()),
      run(supabase.from('v_finanzas_cobranza_mensual').select('*').eq('periodo', startStr).order('monto', { ascending: false })),
      run(
        supabase.from('v_finanzas_factura').select('*')
          .gte('fecha_emision', startStr)
          .lte('fecha_emision', endStr)
          .order('fecha_emision')
          .order('invoice_number')
      ),
    ]);
    return { periodo: startStr, label, mensual, cobranza: cobranza || [], facturas: facturas || [] };
  });
}

/** Every flagged invoice, once, with the reasons it was flagged. */
export function fetchFinanzasExcepciones() {
  return withViews(async () => {
    const rows = await run(
      supabase.from('v_finanzas_factura').select('*')
        .or('flag_sobrepago.eq.true,flag_anulada_con_pago.eq.true,flag_descuadre.eq.true,flag_drift.eq.true')
        .order('fecha_emision', { ascending: false, nullsFirst: false })
    );
    // An invoice that trips two flags is still one exception — keyed by id.
    const byId = new Map();
    for (const row of rows || []) {
      if (byId.has(row.id)) continue;
      const motivos = Object.keys(MOTIVO_LABELS).filter((flag) => row[flag] === true).map((flag) => MOTIVO_LABELS[flag]);
      byId.set(row.id, { ...row, motivos });
    }
    return [...byId.values()];
  });
}

/**
 * Packages in one invoice's box for one client. Not a hook on purpose: the
 * Manifiesto drill calls it lazily on the first open of a box and caches it,
 * so it is never fired once per invoice row.
 */
export function fetchCajaContenido(boxId, clientId) {
  return withViews(async () => {
    const rows = await run(
      supabase.from('v_finanzas_caja_contenido').select('*')
        .eq('box_id', boxId)
        .eq('client_id', clientId)
        .order('numero_paquete', { ascending: true, nullsFirst: false })
    );
    return rows || [];
  });
}

/** Per-operator figures for one calendar month, highest ingreso first. */
export function fetchOperadoresMes(periodo) {
  return withViews(async () => {
    const { startStr } = calendarMonthRange(periodo);
    const rows = await run(
      supabase.from('v_finanzas_operador_mensual').select('*')
        .eq('periodo', startStr)
        .order('ingreso', { ascending: false })
        .order('operador')
    );
    return rows || [];
  });
}

/** Tax captured as a manual adjustment in that month, or null when there is none. */
export function fetchAlertaClasificacion(periodo) {
  return withViews(async () => {
    const { startStr } = calendarMonthRange(periodo);
    return run(supabase.from('v_finanzas_alerta_clasificacion').select('*').eq('periodo', startStr).maybeSingle());
  });
}

/**
 * Every Mes figure for an arbitrary inclusive range, aggregated server-side by
 * finanzas_rango (sql/create_finanzas_rango_functions.sql). Returns
 * { resumen, cobrado, cobranza, operadores, alerta } — cobrado and cobranza are
 * payment-date grain, the rest issue-date grain.
 */
export function fetchRangoFinanzas(desde, hasta) {
  return withViews(() => run(supabase.rpc('finanzas_rango', { p_desde: desde, p_hasta: hasta })));
}

/** Invoices issued in an inclusive range — for display only, never summed. */
export function fetchFacturasRango(desde, hasta) {
  return withViews(async () => {
    const rows = await run(
      supabase.from('v_finanzas_factura').select('*')
        .gte('fecha_emision', desde)
        .lte('fecha_emision', hasta)
        .order('fecha_emision')
        .order('invoice_number')
    );
    return rows || [];
  });
}

/**
 * Revenue chart buckets: [{ bucket, facturas, anuladas, ingreso }], one row per
 * bucket that has invoices. Months come straight from v_finanzas_mensual; days
 * and weeks from finanzas_buckets, which groups server-side.
 */
export function fetchIngresoSerie(desde, hasta, grano) {
  return withViews(async () => {
    if (grano === 'month') {
      const rows = await run(
        supabase.from('v_finanzas_mensual').select('periodo, facturas, anuladas, ingreso')
          .gte('periodo', calendarMonthRange(desde).startStr)
          .lte('periodo', calendarMonthRange(hasta).startStr)
          .order('periodo')
      );
      return (rows || []).map((r) => ({ bucket: r.periodo, facturas: r.facturas, anuladas: r.anuladas, ingreso: r.ingreso }));
    }
    const rows = await run(supabase.rpc('finanzas_buckets', { p_desde: desde, p_hasta: hasta, p_grano: grano }));
    return rows || [];
  });
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useManifiestoFinanzas(manifestId) {
  const [state, setState] = useState(manifestId ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!manifestId) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchManifiestoFinanzas(manifestId);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useManifiestoFinanzas:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.manifiesto });
    }
  }, [manifestId]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useManifiestosFinanzas(limit = 24) {
  const [state, setState] = useState(LOADING);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchManifiestosFinanzas(limit);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useManifiestosFinanzas:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.manifiestos });
    }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useMesFinanzas(periodo) {
  const [state, setState] = useState(periodo ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!periodo) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchMesFinanzas(periodo);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useMesFinanzas:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.mes });
    }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useFinanzasExcepciones() {
  const [state, setState] = useState(LOADING);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchFinanzasExcepciones();
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useFinanzasExcepciones:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.excepciones });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useOperadoresMes(periodo) {
  const [state, setState] = useState(periodo ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!periodo) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchOperadoresMes(periodo);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useOperadoresMes:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.operadores });
    }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useAlertaClasificacion(periodo) {
  const [state, setState] = useState(periodo ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!periodo) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchAlertaClasificacion(periodo);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useAlertaClasificacion:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.alerta });
    }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useRangoFinanzas(desde, hasta) {
  const enabled = !!(desde && hasta);
  const [state, setState] = useState(enabled ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!desde || !hasta) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchRangoFinanzas(desde, hasta);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useRangoFinanzas:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.rango });
    }
  }, [desde, hasta]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useFacturasRango(desde, hasta) {
  const enabled = !!(desde && hasta);
  const [state, setState] = useState(enabled ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!desde || !hasta) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchFacturasRango(desde, hasta);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useFacturasRango:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.facturas });
    }
  }, [desde, hasta]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}

export function useIngresoSerie(desde, hasta, grano) {
  const enabled = !!(desde && hasta && GRANOS.includes(grano));
  const [state, setState] = useState(enabled ? LOADING : IDLE);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!desde || !hasta || !GRANOS.includes(grano)) { setState(IDLE); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fetchIngresoSerie(desde, hasta, grano);
      if (seq.current === mySeq) setState(settled(result));
    } catch (err) {
      console.error('[FINANZAS] useIngresoSerie:', err);
      if (seq.current === mySeq) setState({ ...IDLE, error: ERROR_MSG.serie });
    }
  }, [desde, hasta, grano]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}
